import { motion } from 'framer-motion';
import { Send, Bot, MessageCircle } from 'lucide-react';
import { Button } from './ui/button';
import { useGateModal } from '../hooks/useGateModal';

const HowItWorks = () => {
  const { openGate } = useGateModal();

  const steps = [
    {
      number: '01',
      icon: MessageCircle,
      title: 'Jawab Quiz Singkat',
      description: 'Jawab beberapa pertanyaan tentang gaya trading Anda. Tidak ada jawaban benar atau salah.',
      action: 'Mulai Quiz',
      onClick: openGate,
      color: 'from-blue-500/20 to-transparent',
    },
    {
      number: '02',
      icon: Send,
      title: 'Lihat Hasil & Gabung',
      description: 'Selesaikan quiz, dapatkan strategi yang cocok, lalu langsung gabung Telegram untuk akses bot AI.',
      action: null,
      onClick: null,
      color: 'from-purple-500/20 to-transparent',
    },
    {
      number: '03',
      icon: Bot,
      title: 'Gunakan Bot AI',
      description: 'Ketik /scalper, /intraday, atau /swing di grup Telegram. Bot akan menganalisis chart XAUUSD dan memberikan setup edukasi.',
      action: null,
      onClick: null,
      color: 'from-teal-500/20 to-transparent',
    },
  ];

  return (
    <section
      className="py-24 md:py-32 relative"
      data-testid="how-it-works-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-20"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-tc-primary/10 border border-tc-primary/20 mb-6">
            <span className="text-sm text-tc-primary font-medium">CARA MULAI</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-heading font-bold text-white mb-6">
            3 langkah untuk akses bot AI
          </h2>
          <p className="text-xl text-tc-text-secondary max-w-2xl mx-auto">
            Dari daftar sampai analisis chart pertama dalam hitungan menit
          </p>
        </motion.div>

        {/* Steps */}
        <div className="grid md:grid-cols-3 gap-8">
          {steps.map((step, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: idx * 0.15 }}
              className="group"
            >
              <div className="glass-card rounded-2xl p-8 h-full relative overflow-hidden">
                <div className={`absolute inset-0 bg-gradient-to-br ${step.color} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
                <div className="relative">
                  {/* Step number & icon */}
                  <div className="flex items-center justify-between mb-6">
                    <span className="text-6xl font-heading font-extrabold text-tc-surface-highlight group-hover:text-tc-primary/20 transition-colors">
                      {step.number}
                    </span>
                    <div className="w-12 h-12 rounded-xl bg-tc-primary/10 flex items-center justify-center group-hover:bg-tc-primary/20 transition-colors">
                      <step.icon className="w-6 h-6 text-tc-primary" />
                    </div>
                  </div>

                  <h3 className="text-xl font-heading font-semibold text-white mb-4">
                    {step.title}
                  </h3>
                  <p className="text-tc-text-secondary mb-6 leading-relaxed">
                    {step.description}
                  </p>

                  {/* Action button */}
                  {step.action && (
                    <Button
                      onClick={step.onClick}
                      variant="outline"
                      className="border-tc-primary/30 text-tc-primary hover:bg-tc-primary/10 rounded-full text-sm"
                      data-testid={`how-step-${idx}-btn`}
                    >
                      {step.action}
                    </Button>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Connector line visual for desktop */}
        <div className="hidden md:flex justify-center mt-8">
          <div className="flex items-center gap-2 text-tc-text-secondary text-sm">
            <span className="w-2 h-2 rounded-full bg-tc-primary" />
            <span className="w-16 h-px bg-tc-border" />
            <span className="w-2 h-2 rounded-full bg-tc-primary" />
            <span className="w-16 h-px bg-tc-border" />
            <span className="w-2 h-2 rounded-full bg-tc-primary" />
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
